import {Autocomplete, Select} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import React, {useCallback, useEffect, useState} from 'react';
import type {
  ItemAssignmentSimpleResponse,
  NexusGraphRequest,
} from 'src/api/ProactApiClient';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useLazyGetCourseSessionListQuery} from 'src/redux/queries/courseSession';
import {useLazyGetFormListQuery} from 'src/redux/queries/form';
import {useLazyGetItemAssignmentListQuery} from 'src/redux/queries/itemAssignment';
import {useLazyGetTaskDefinitionListByRequestQuery} from 'src/redux/queries/taskDefinition';
import {useGetWorkflowListQuery} from 'src/redux/queries/workflow';
import {useAlert} from 'src/shared/hooks/useAlert';
import useDebounce from 'src/shared/hooks/useDebounce';

type LazyGraphTrigger = (arg: NexusGraphRequest) => {
  unwrap: () => Promise<unknown>;
};

interface NexusEntitySearchProps {
  triggerGraph: LazyGraphTrigger;
  onSearchSelect?: (entityType: string, entityId: number) => void;
}

interface SearchResult {
  id: number;
  label: string;
  entityType: string;
}

const entityTypeOptions = [
  {value: 'workflow', label: 'Workflow'},
  {value: 'task', label: 'Task'},
  {value: 'form', label: 'Form'},
  {value: 'trainingSession', label: 'Training Session'},
  {value: 'itemAssignment', label: 'Item Assignment'},
];

export const NexusEntitySearch: React.FC<NexusEntitySearchProps> = ({
  triggerGraph,
  onSearchSelect,
}) => {
  const {setAlert} = useAlert();
  const [entityType, setEntityType] = useState<string>('workflow');
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const [triggerItemAssignmentList] = useLazyGetItemAssignmentListQuery();
  const [triggerTaskDefinitionList] =
    useLazyGetTaskDefinitionListByRequestQuery();
  const [triggerFormList] = useLazyGetFormListQuery();
  const [triggerCourseSessionList] = useLazyGetCourseSessionListQuery();

  const debouncedSearch = useDebounce(searchTerm, 400);

  const isWorkflowSearch =
    entityType === 'workflow' &&
    !!debouncedSearch &&
    debouncedSearch.length >= 2;

  const {data: workflowData, isFetching: isWorkflowFetching} =
    useGetWorkflowListQuery(
      {
        start: 0,
        length: 10,
        search: debouncedSearch,
      },
      {skip: !isWorkflowSearch}
    );

  useEffect(() => {
    if (entityType !== 'workflow') return;

    if (!debouncedSearch || debouncedSearch.length < 2) {
      setResults([]);
      return;
    }

    if (!isWorkflowFetching && workflowData) {
      const searchResults: SearchResult[] =
        workflowData.data?.map(w => ({
          id: w.id,
          label: `${w.workflowDefinition?.name ?? 'Workflow'} — ${
            w.internalIdentifier ?? w.id
          }`,
          entityType: 'workflow',
        })) ?? [];
      setResults(searchResults);
    }
  }, [entityType, debouncedSearch, workflowData, isWorkflowFetching]);

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) {
      setResults([]);
      return;
    }

    if (entityType === 'workflow') return;

    const search = async () => {
      setIsSearching(true);
      try {
        let searchResults: SearchResult[] = [];

        if (entityType === 'task') {
          const response = await triggerTaskDefinitionList({
            start: 0,
            length: 10,
            search: debouncedSearch,
            includeDemonstrationGenerated: false,
          }).unwrap();
          searchResults =
            response.data?.map(t => ({
              id: t.id,
              label: t.subject ?? `Task ${t.id}`,
              entityType: 'task',
            })) ?? [];
        } else if (entityType === 'form') {
          const response = await triggerFormList({
            start: 0,
            length: 10,
            search: debouncedSearch,
          }).unwrap();
          searchResults =
            response.data?.map(f => ({
              id: f.id,
              label: `${f.formDefinition?.name ?? 'Form'} — ${
                f.internalIdentifier ?? f.id
              }`,
              entityType: 'form',
            })) ?? [];
        } else if (entityType === 'trainingSession') {
          const response = await triggerCourseSessionList({
            start: 0,
            length: 10,
            search: debouncedSearch,
            searchAllUsers: true,
          }).unwrap();
          searchResults =
            response.data?.map(s => ({
              id: s.id,
              label: `${s.courseDefinition?.name ?? 'Training Session'} — ${
                s.internalIdentifier ?? s.id
              } — ${s.traineeName ?? ''}`,
              entityType: 'trainingSession',
            })) ?? [];
        } else if (entityType === 'itemAssignment') {
          const response = await triggerItemAssignmentList({
            start: 0,
            length: 10,
            search: debouncedSearch,
          }).unwrap();
          searchResults =
            response.data?.map((i: ItemAssignmentSimpleResponse) => ({
              id: i.id,
              label: `${i.itemDefinition?.name ?? 'Item'} — ${
                i.internalIdentifier ?? i.id
              } — ${i.assignedToUser?.name ?? ''}`,
              entityType: 'itemAssignment',
            })) ?? [];
        }

        setResults(searchResults);
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      }
      setIsSearching(false);
    };

    void search();
  }, [debouncedSearch, entityType, setAlert]);

  const handleSelectResult = useCallback(
    async (value: string) => {
      if (!value) return;
      const result = results.find(r => String(r.id) === value);
      if (!result) return;

      onSearchSelect?.(result.entityType, result.id);

      try {
        await triggerGraph({
          entityType: result.entityType,
          entityId: result.id,
          maxDepth: 5,
        }).unwrap();
      } catch (error) {
        const status =
          error != null && typeof error === 'object' && 'status' in error
            ? (error as {status: unknown}).status
            : undefined;
        if (status === 404) {
          setAlert('No workflow connections found for this entity.', 'info');
        } else {
          setAlert(parseErrorMessage(error), 'error');
        }
      }
    },
    [results, triggerGraph, setAlert, onSearchSelect]
  );

  const isBusy = entityType === 'workflow' ? isWorkflowFetching : isSearching;

  return (
    <Group gap="sm" align="center" wrap="nowrap">
      <Select
        data={entityTypeOptions}
        value={entityType}
        onChange={val => {
          setEntityType(val ?? 'workflow');
          setSearchTerm('');
          setResults([]);
        }}
        w={{base: 100, sm: 140}}
        size="xs"
      />
      <Autocomplete
        placeholder={`Search ${
          entityType === 'trainingSession'
            ? 'training sessions'
            : entityType === 'itemAssignment'
            ? 'item assignments'
            : entityType + 's'
        } by name...`}
        value={searchTerm}
        onChange={setSearchTerm}
        onOptionSubmit={handleSelectResult}
        data={
          results.length > 0
            ? results.map(r => ({value: String(r.id), label: r.label}))
            : debouncedSearch.length >= 2 && !isBusy
            ? [{value: '', label: 'No results found'}]
            : []
        }
        rightSection={isBusy ? <Loader size="xs" /> : undefined}
        w={{base: 160, sm: 270}}
        size="xs"
      />
    </Group>
  );
};
